import { RoleType } from "@/types/enums";
import { AppointmentChecklistType } from "@/types/enums/appointment";
import { ProviderSummary, RoleName } from "@/types/provider";

/**
 * Permission checking utilities for the frontend
 */

/**
 * Helper function to check if user has admin privileges
 */
export function userIsAdmin(user: ProviderSummary | null | undefined): boolean {
  if (!user?.roles) return false;
  return user.roles.some((role) => role.userType === RoleType.ADMIN);
}

/**
 * Check if a user has a specific permission
 */
export function hasPermission(
  user: ProviderSummary | null | undefined,
  permission: string,
): boolean {
  if (!user) return false;

  // Admin users have all permissions
  if (userIsAdmin(user)) return true;

  const permsSet = new Set(user.permissions);
  return permsSet.has("*") || permsSet.has(permission);
}

/**
 * Check if a user has any of the specified permissions
 */
export function hasAnyPermission(
  user: ProviderSummary | null | undefined,
  permissions: string[],
): boolean {
  if (!user) return false;

  // Admin users have all permissions
  if (userIsAdmin(user)) return true;

  const permsSet = new Set(user.permissions);
  return (
    permsSet.has("*") ||
    permissions.some((permission) => permsSet.has(permission))
  );
}

/**
 * Check if a user has all of the specified permissions
 */
export function hasAllPermissions(
  user: ProviderSummary | null | undefined,
  permissions: string[],
): boolean {
  if (!user) return false;

  // Admin users have all permissions
  if (userIsAdmin(user)) return true;

  const permsSet = new Set(user.permissions);
  return (
    permsSet.has("*") ||
    permissions.every((permission) => permsSet.has(permission))
  );
}

/**
 * Whether a user can add photos to a patient's gallery, or to a note when one is
 * given. Mirrors the attachment upload routes: gallery photos accept either
 * permission, but putting a photo on a note needs PATIENTS_NOTES_UPLOAD.
 */
export function canAddPhotos(
  user: ProviderSummary | null | undefined,
  noteId?: string | null,
): boolean {
  return noteId
    ? hasPermission(user, PERMISSIONS.PATIENTS_NOTES_UPLOAD)
    : hasAnyPermission(user, [
        PERMISSIONS.CLINICAL_GALLERY_WRITE,
        PERMISSIONS.PATIENTS_NOTES_UPLOAD,
      ]);
}

/**
 * Which photo menu actions a user can take. Mirrors the attachment routes:
 * renaming accepts the gallery permission, duplicating needs note upload, and
 * everything else that changes a photo (annotate, delete, move, tag, share)
 * needs note write.
 */
export function galleryPhotoActions(user: ProviderSummary | null | undefined): {
  canEdit: boolean;
  canDuplicate: boolean;
  canRename: boolean;
} {
  const canEdit = hasPermission(user, PERMISSIONS.PATIENTS_NOTES_WRITE);
  return {
    canEdit,
    canDuplicate: hasPermission(user, PERMISSIONS.PATIENTS_NOTES_UPLOAD),
    canRename:
      canEdit || hasPermission(user, PERMISSIONS.CLINICAL_GALLERY_WRITE),
  };
}

export const NO_PHOTO_PERMISSION_REASON =
  "You don't have permission to add photos";

/**
 * Check a page's permission gate: any of `permissions`, plus every one of
 * `allPermissions`.
 */
export function hasPagePermissions(
  user: ProviderSummary | null | undefined,
  page: { permissions?: string[]; allPermissions?: string[] },
): boolean {
  return (
    (!page.permissions || hasAnyPermission(user, page.permissions)) &&
    (!page.allPermissions || hasAllPermissions(user, page.allPermissions))
  );
}

/**
 * Check if a user has a specific role
 */
export function hasRole(
  user: ProviderSummary | null | undefined,
  role: RoleName,
): boolean {
  if (!user) return false;

  return (
    user.roles?.some(
      (userRole) => userRole.name.toLowerCase() === role.toLowerCase(),
    ) || false
  );
}

/**
 * Check if a user has any of the specified roles
 */
export function hasAnyRole(
  user: ProviderSummary | null | undefined,
  roles: RoleName[],
): boolean {
  if (!user) return false;

  return roles.some((role) => hasRole(user, role));
}

/**
 * Get all permissions for a user (from all their roles)
 */
export function getUserPermissions(
  user: ProviderSummary | null | undefined,
): string[] {
  if (!user) return [];

  // Admin users have all permissions (represented by wildcard)
  if (userIsAdmin(user)) return ["*"];

  // Return user's direct permissions or empty array
  return user.permissions || [];
}

/**
 * Check if user is admin
 */
export function isAdmin(user: ProviderSummary | null | undefined): boolean {
  return userIsAdmin(user);
}

/**
 * Permission constants matching backend enum.
 * Permissions are organized alphabetically by category.
 */
export const PERMISSIONS = {
  // AI features
  AI_USE: "ai:use",
  AI_ANALYTICS_USE: "ai:analytics:use",
  AI_AGENT_USE: "ai:agent:use",
  AI_SCRIBE_USE: "ai:scribe:use",

  // Analytics permissions
  ANALYTICS_EXPORT: "analytics:export",
  ANALYTICS_READ: "analytics:read",
  ANALYTICS_WRITE: "analytics:write",
  ANALYTICS_LOCATION_GOALS_WRITE: "analytics:location_goals:write",
  // One per built-in dashboard; analytics:read covers custom dashboards.
  ANALYTICS_DASHBOARD_ACCOUNTING_OVERVIEW_READ:
    "analytics:dashboards:accounting_overview:read",
  ANALYTICS_DASHBOARD_APPOINTMENT_DASHBOARD_READ:
    "analytics:dashboards:appointment_dashboard:read",
  ANALYTICS_DASHBOARD_BLAST_ANALYTICS_READ:
    "analytics:dashboards:blast_analytics:read",
  ANALYTICS_DASHBOARD_CALL_ANALYTICS_READ:
    "analytics:dashboards:call_analytics:read",
  ANALYTICS_DASHBOARD_CARE_PLAN_ANALYTICS_READ:
    "analytics:dashboards:care_plan_analytics:read",
  ANALYTICS_DASHBOARD_CASH_REGISTER_DRILL_DOWN_READ:
    "analytics:dashboards:cash_register_drill_down:read",
  ANALYTICS_DASHBOARD_CASH_REGISTER_REPORT_READ:
    "analytics:dashboards:cash_register_report:read",
  ANALYTICS_DASHBOARD_INVENTORY_READ: "analytics:dashboards:inventory:read",
  ANALYTICS_DASHBOARD_LOCATION_SCORECARD_READ:
    "analytics:dashboards:location_scorecard:read",
  ANALYTICS_DASHBOARD_MEMBERSHIPS_READ: "analytics:dashboards:memberships:read",
  ANALYTICS_DASHBOARD_NOTES_READ: "analytics:dashboards:notes:read",
  ANALYTICS_DASHBOARD_PATIENT_BREAKDOWN_READ:
    "analytics:dashboards:patient_breakdown:read",
  ANALYTICS_DASHBOARD_PAYMENT_BREAKDOWN_READ:
    "analytics:dashboards:payment_breakdown:read",
  ANALYTICS_DASHBOARD_PAYMENT_PLAN_ANALYTICS_READ:
    "analytics:dashboards:payment_plan_analytics:read",
  ANALYTICS_DASHBOARD_PRACTICE_LEAD_READ:
    "analytics:dashboards:practice_lead:read",
  ANALYTICS_DASHBOARD_PRODUCT_SALES_BREAKDOWN_READ:
    "analytics:dashboards:product_sales_breakdown:read",
  ANALYTICS_DASHBOARD_PROVIDER_ACTIVITY_READ:
    "analytics:dashboards:provider_activity:read",
  ANALYTICS_DASHBOARD_PROVIDER_EARNINGS_READ:
    "analytics:dashboards:provider_earnings:read",
  ANALYTICS_DASHBOARD_SALES_TAX_DETAILS_READ:
    "analytics:dashboards:sales_tax_details:read",

  APPROVALS_REVIEW: "approvals:review",
  APPROVALS_POLICIES_WRITE: "approvals:policies:write",

  // Billing permissions
  BILLING_CHARGES_EXPORT: "billing:charges:export",
  BILLING_CHARGES_SOLD_BY_WRITE: "billing:charges:sold_by:write",
  BILLING_CHARGES_ADJUST: "billing:charges:adjust",
  BILLING_CHARGES_WRITE_OFF: "billing:charges:write_off",
  BILLING_COMMISSIONS_EXPORT: "billing:commissions:export",
  BILLING_COMMISSIONS_READ: "billing:commissions:read",
  BILLING_COMMISSIONS_REPORTS_READ: "billing:commissions:reports:read",
  BILLING_COMMISSIONS_REPORTS_WRITE: "billing:commissions:reports:write",
  BILLING_COMMISSIONS_WRITE: "billing:commissions:write",
  BILLING_PAY_VIEW_ALL: "billing:pay:view_all",
  BILLING_DEPOSITS_READ: "billing:deposits:read",
  BILLING_DISCOUNTS_APPLY: "billing:discounts:apply",
  BILLING_DISCOUNTS_BYPASS_REASON: "billing:discounts:bypass_reason",
  BILLING_PRICES_INCREASE: "billing:prices:increase",
  BILLING_PRICES_OVERRIDE: "billing:prices:override",
  BILLING_BANKED_ITEMS_MANAGE: "billing:banked_items:manage",
  DISCOUNTS_TRANSFER: "discounts:transfer",
  PATIENT_CREDITS_TRANSFER: "patient_credits:transfer",
  LOYALTY_POINTS_ADJUST: "loyalty_points:adjust",
  BILLING_BALANCES_ADJUST: "billing:balances:adjust",
  PROMOTIONS_READ: "promotions:read",
  PROMOTIONS_WRITE: "promotions:write",
  REFERRAL_PROGRAMS_READ: "referral_programs:read",
  REFERRAL_PROGRAMS_WRITE: "referral_programs:write",
  BILLING_INVOICES_READ: "billing:invoices:read",
  BILLING_INVOICES_WRITE: "billing:invoices:write",
  BILLING_PAYMENTS_EXPORT: "billing:payments:export",
  BILLING_PAYMENTS_PROCESS: "billing:payments:process",
  BILLING_PAYMENTS_REFUND: "billing:payments:refund",
  BILLING_PAYMENTS_REFUND_CREDIT: "billing:payments:refund:credit",
  BILLING_PAYMENTS_DELETE: "billing:payments:delete",
  BILLING_PACKAGES_CANCEL: "billing:packages:cancel",
  BILLING_CHARGEBACKS_READ: "billing:chargebacks:read",
  BILLING_CHARGEBACKS_MANAGE: "billing:chargebacks:manage",
  BILLING_READ: "billing:read",
  BILLING_CHARGES_VIEW_ALL: "billing:charges:view_all",
  BILLING_REPORTS_READ: "billing:reports:read",

  // Clinical permissions
  CLINICAL_EPRESCRIBING_ENROL_SELF: "clinical:eprescribing:enrol_self",
  CLINICAL_EPRESCRIBING_READ: "clinical:eprescribing:read",
  CLINICAL_EPRESCRIBING_PRESETS_WRITE: "clinical:eprescribing:presets:write",
  CLINICAL_GALLERY_DOWNLOAD: "clinical:gallery:download",
  CLINICAL_GALLERY_READ: "clinical:gallery:read",
  CLINICAL_GALLERY_WRITE: "clinical:gallery:write",
  CLINICAL_NOTES_READ: "clinical:notes:read",

  // Downloads permissions
  DOWNLOADS_EXPORT: "downloads:export",

  // Clock-in permissions
  CLOCK_IN_EXPORT: "clock_in:export",
  CLOCK_IN_HISTORY_READ: "clock_in:history:read",
  CLOCK_IN_HISTORY_READ_ALL: "clock_in:history:read_all",
  CLOCK_IN_SKIP: "clock_in:skip",
  CLOCK_IN_TIMES_EDIT: "clock_in:times:edit",

  // Forms permissions
  FORMS_SUBMISSIONS_READ: "forms:submissions:read",
  FORMS_SUBMISSIONS_WRITE: "forms:submissions:write",
  FORMS_TEMPLATES_READ: "forms:templates:read",
  FORMS_TEMPLATES_WRITE: "forms:templates:write",

  // Inventory permissions. INVENTORY_WRITE is the administer tier (edit/delete
  // shipments, purchase orders and transfers, vendors, stock configuration) and
  // implies the narrower tiers below, so a role holding it keeps everything.
  INVENTORY_READ: "inventory:read",
  INVENTORY_WRITE: "inventory:write",
  INVENTORY_COST: "inventory:cost",
  INVENTORY_RECEIVE: "inventory:receive",
  INVENTORY_ORDERS_PLACE: "inventory:orders:place",
  INVENTORY_ORDERS_APPROVE: "inventory:orders:approve",
  INVENTORY_TRANSFER: "inventory:transfer",
  INVENTORY_COUNT: "inventory:count",
  INVENTORY_DRAWDOWN: "inventory:drawdown",
  INVENTORY_ADDRESSES_WRITE: "inventory:addresses:write",

  // Labs permissions. Viewing results and placing an order are separate authorities:
  // LABS_READ does not imply LABS_ORDERS_WRITE.
  LABS_READ: "labs:read",
  LABS_PANELS_WRITE: "labs:panels:write",
  LABS_ORDERS_WRITE: "labs:orders:write",
  LABS_UPLOAD_WRITE: "labs:upload:write",

  // Location access permissions
  LOCATIONS_SEE_ALL: "locations:see_all",

  // Memberships permissions
  MEMBERSHIPS_READ: "memberships:read",
  MEMBERSHIPS_WRITE: "memberships:write",
  MEMBERSHIPS_ENROLLMENTS_MANAGE: "memberships:enrollments:manage",
  MEMBERSHIPS_ENROLLMENTS_WRITE: "memberships:enrollments:write",
  MEMBERSHIPS_ENROLLMENTS_FREEZE: "memberships:enrollments:freeze",
  MEMBERSHIPS_ENROLLMENTS_CANCEL: "memberships:enrollments:cancel",
  MEMBERSHIPS_ENROLLMENTS_PRICE: "memberships:enrollments:price",

  // Messaging permissions
  MESSAGING_BLASTS_READ: "messaging:blasts:read",
  MESSAGING_BLASTS_WRITE: "messaging:blasts:write",
  MESSAGING_CALLS_MAKE: "messaging:calls:make",
  MESSAGING_CALLS_READ: "messaging:calls:read",
  MESSAGING_CHATS_READ: "messaging:chats:read",
  MESSAGING_CHATS_WRITE: "messaging:chats:write",
  MESSAGING_EMAILS_READ: "messaging:emails:read",
  MESSAGING_EMAILS_WRITE: "messaging:emails:write",
  MESSAGING_EMAILS_READ_ALL: "messaging:emails:read_all",
  MESSAGING_FAX_READ: "messaging:fax:read",
  MESSAGING_FAX_WRITE: "messaging:fax:write",
  MESSAGING_FAX_CONTACTS_WRITE: "messaging:fax:contacts:write",
  MESSAGING_SEQUENCES_READ: "messaging:sequences:read",
  MESSAGING_SEQUENCES_WRITE: "messaging:sequences:write",
  MESSAGING_SOCIAL_READ: "messaging:social:read",
  MESSAGING_SOCIAL_WRITE: "messaging:social:write",

  // Care plan permissions
  CARE_PLAN_TEMPLATES_WRITE: "care_plan_templates:write",

  // Patient permissions
  PATIENTS_CREATE: "patients:create",
  CHECKLIST_CHECKED_IN_WRITE: "checklist:checked_in:write",
  CHECKLIST_CARE_PLAN_WRITE: "checklist:care_plan:write",
  CHECKLIST_DOSE_TAKEN_WRITE: "checklist:dose_taken:write",
  CHECKLIST_FILLED_FORM_WRITE: "checklist:filled_form:write",
  CHECKLIST_MEASUREMENT_TAKEN_WRITE: "checklist:measurement_taken:write",
  CHECKLIST_NOTE_TAKEN_WRITE: "checklist:note_taken:write",
  CHECKLIST_PAYMENT_MADE_WRITE: "checklist:payment_made:write",
  CHECKLIST_PICTURE_TAKEN_WRITE: "checklist:picture_taken:write",
  CHECKLIST_REBOOK_WRITE: "checklist:rebook:write",
  CHECKLIST_REQUEST_GFE_WRITE: "checklist:request_gfe:write",
  CHECKLIST_WITH_MA_WRITE: "checklist:with_ma:write",
  CHECKLIST_WITH_PROVIDER_WRITE: "checklist:with_provider:write",
  PATIENTS_DOSES_PROVIDER_WRITE: "patients:doses:provider:write",
  PATIENTS_DOSES_CREATE: "patients:doses:create",
  PATIENTS_DOSES_EDIT: "patients:doses:edit",
  PATIENTS_DOSES_PRINT: "patients:doses:print",
  PATIENTS_DOSES_DELETE: "patients:doses:delete",
  PATIENTS_NOTES_ARCHIVE: "patients:notes:archive",
  PATIENTS_NOTES_UPLOAD: "patients:notes:upload",
  PATIENTS_NOTES_WRITE: "patients:notes:write",
  PATIENTS_PROFILES_READ: "patients:profiles:read",
  PATIENTS_CONTACT_INFO_READ: "patients:contact_info:read",
  PATIENTS_PROFILES_WRITE: "patients:profiles:write",
  PATIENTS_EXPORT: "patients:export",
  EXPORTS_PATIENTS_DIRECT: "exports:patients:direct",
  PATIENTS_SURCHARGE_EXEMPTION_WRITE: "patients:surcharge_exemption:write",
  PATIENTS_DOCUMENTS_READ: "patients:documents:read",
  PATIENTS_DOCUMENTS_MANAGE: "patients:documents:manage",
  PATIENTS_DOCUMENTS_REVIEW: "patients:documents:review",
  PATIENTS_READ: "patients:read",

  // QuickBooks permissions
  QUICKBOOKS_MANAGE: "quickbooks:manage",
  QUICKBOOKS_POST: "quickbooks:post",

  // Review permissions
  REVIEWS_READ: "reviews:read",

  // Scheduling permissions
  SCHEDULING_APPOINTMENTS_COMPLETE: "scheduling:appointments:complete",
  SCHEDULING_APPOINTMENTS_DELETE: "scheduling:appointments:delete",
  SCHEDULING_APPOINTMENTS_WRITE: "scheduling:appointments:write",
  SCHEDULING_BLOCKS_WRITE: "scheduling:blocks:write",
  SCHEDULING_BLOCKS_MOVE: "scheduling:blocks:move",
  SCHEDULING_BLOCKS_BUSINESS_WRITE: "scheduling:blocks:business:write",
  SCHEDULING_BOOKING_FEE_CHANGE_METHOD: "scheduling:booking_fee:change_method",
  SCHEDULING_BOOKING_FEE_SKIP: "scheduling:booking_fee:skip",
  SCHEDULING_CARD_ON_FILE_SKIP: "scheduling:card_on_file:skip",
  SCHEDULING_CANCELLATION_FEE_SKIP: "scheduling:cancellation_fee:skip",
  SCHEDULING_NO_SHOW_FEE_SKIP: "scheduling:no_show_fee:skip",
  SCHEDULING_OVERRIDE_BLOCK_CONSTRAINTS:
    "scheduling:override_constraints:blocks",
  SCHEDULING_OVERRIDE_CAPACITY_CONSTRAINTS:
    "scheduling:override_constraints:capacity",
  SCHEDULING_OVERRIDE_SERVICE_PROVIDER_CONSTRAINTS:
    "scheduling:override_constraints:service_provider",
  SCHEDULING_OVERRIDE_SHIFT_CONSTRAINTS:
    "scheduling:override_constraints:shifts",
  SCHEDULING_READ: "scheduling:read",
  SCHEDULING_WAITLIST_MANAGE: "scheduling:waitlist:manage",
  SCHEDULING_SHIFTS_WRITE: "scheduling:shifts:write",

  // Settings - AI Assistants
  SETTINGS_ASSISTANTS_INBOUND_READ: "settings:assistants:inbound:read",
  SETTINGS_ASSISTANTS_INBOUND_WRITE: "settings:assistants:inbound:write",
  SETTINGS_ASSISTANTS_OUTBOUND_READ: "settings:assistants:outbound:read",
  SETTINGS_ASSISTANTS_OUTBOUND_WRITE: "settings:assistants:outbound:write",
  SETTINGS_ASSISTANTS_SCRIBE_READ: "settings:assistants:scribe:read",
  SETTINGS_ASSISTANTS_SCRIBE_WRITE: "settings:assistants:scribe:write",
  SETTINGS_ASSISTANTS_SUMMARIZER_READ: "settings:assistants:summarizer:read",
  SETTINGS_ASSISTANTS_SUMMARIZER_WRITE: "settings:assistants:summarizer:write",
  SETTINGS_ASSISTANTS_TEXTING_READ: "settings:assistants:texting:read",
  SETTINGS_ASSISTANTS_TEXTING_WRITE: "settings:assistants:texting:write",
  // Settings - Coupa
  SETTINGS_COUPA_READ: "settings:coupa:read",
  SETTINGS_COUPA_WRITE: "settings:coupa:write",
  SETTINGS_COUPA_RULES_OVERRIDE: "settings:coupa:rules:override",
  // Settings - Developers
  SETTINGS_DEVELOPERS_READ: "settings:developers:read",
  SETTINGS_DEVELOPERS_WRITE: "settings:developers:write",

  // Settings - Document Types
  SETTINGS_DOCUMENT_TYPES_WRITE: "settings:document_types:write",

  // Settings - Email Domain
  SETTINGS_EMAIL_DOMAIN_READ: "settings:email_domain:read",
  SETTINGS_EMAIL_DOMAIN_WRITE: "settings:email_domain:write",

  // Settings - Insurance Carriers
  SETTINGS_INSURANCE_READ: "settings:insurance:read",
  SETTINGS_INSURANCE_WRITE: "settings:insurance:write",

  // Settings - Locations
  SETTINGS_LOCATIONS_READ: "settings:locations:read",
  SETTINGS_LOCATIONS_WRITE: "settings:locations:write",

  // Settings - Measurements
  SETTINGS_MEASUREMENTS_READ: "settings:measurements:read",

  // Settings - Note Templates
  SETTINGS_NOTE_TEMPLATES_MANAGE_ACCESS:
    "settings:note_templates:manage_access",
  SETTINGS_NOTE_TEMPLATES_READ: "settings:note_templates:read",
  SETTINGS_NOTE_TEMPLATES_WRITE: "settings:note_templates:write",

  // Settings - Treatment Plan Templates
  SETTINGS_TREATMENT_PLAN_TEMPLATES_READ:
    "settings:treatment_plan_templates:read",
  SETTINGS_TREATMENT_PLAN_TEMPLATES_WRITE:
    "settings:treatment_plan_templates:write",

  // Settings - Payment
  SETTINGS_PAYMENT_READ: "settings:payment:read",
  SETTINGS_PAYMENT_WRITE: "settings:payment:write",

  // Settings - General
  SETTINGS_READ: "settings:read",

  // Settings - Security (sign-out timeout, IP / clock-in network restrictions)
  SETTINGS_SECURITY_WRITE: "settings:security:write",

  // Settings - Medical Record Numbers (generating MRNs is final, so admins only by default)
  SETTINGS_MRN_WRITE: "settings:mrn:write",

  // Settings - Roles
  SETTINGS_ROLES_READ: "settings:roles:read",
  SETTINGS_ROLES_WRITE: "settings:roles:write",

  // Settings - Rules
  SETTINGS_RULES_READ: "settings:rules:read",
  SETTINGS_RULES_WRITE: "settings:rules:write",

  // Settings - Scheduling
  SETTINGS_SCHEDULING_READ: "settings:scheduling:read",
  SETTINGS_SCHEDULING_WRITE: "settings:scheduling:write",
  SETTINGS_SELF_SCHEDULING_WRITE: "settings:self_scheduling:write",

  // Settings - Services
  SETTINGS_SERVICES_READ: "settings:services:read",
  SETTINGS_SERVICES_WRITE: "settings:services:write",
  SETTINGS_SERVICES_DETAILS_WRITE: "settings:services:details:write",
  SETTINGS_BOOKABLES_WRITE: "settings:bookables:write",
  SETTINGS_CALENDAR_APPEARANCE_WRITE: "settings:calendar_appearance:write",

  // Settings - Catalog items (not inventory stock ops)
  SETTINGS_PRODUCTS_WRITE: "settings:products:write",
  SETTINGS_PACKAGES_WRITE: "settings:packages:write",
  SETTINGS_MEDICATIONS_WRITE: "settings:medications:write",
  SETTINGS_GIFT_CARDS_WRITE: "settings:gift_cards:write",

  // Settings - Suggestions
  SETTINGS_SUGGESTIONS_READ: "settings:suggestions:read",
  SETTINGS_SUGGESTIONS_WRITE: "settings:suggestions:write",

  // Settings - Tags
  SETTINGS_TAGS_READ: "settings:tags:read",
  SETTINGS_TAGS_WRITE: "settings:tags:write",

  // Settings - General (write)
  SETTINGS_WRITE: "settings:write",

  // Sign-off permissions
  SIGN_OFF_DOCUMENTS: "sign_off:documents",
  SIGN_OFF_MEDICATIONS: "sign_off:medications",
  SIGN_OFF_NOTES: "sign_off:notes",
  SIGN_OFF_SIGNATURE: "sign_off:signature",

  // Growth (module MARKETING) permissions
  GROWTH_READ: "growth:read",
  GROWTH_WRITE: "growth:write",

  // Task permissions
  TASKS_ASSIGN: "tasks:assign",
  TASKS_READ: "tasks:read",
  TASKS_WRITE: "tasks:write",

  // Telehealth permissions
  TELEHEALTH_VIDEO_USE: "telehealth:video:use",

  // Usage permissions
  USAGE_READ: "usage:read",

  // User management permissions
  USERS_READ: "users:read",
  USERS_WRITE: "users:write",

  // Whisper (internal team chat) permissions
  WHISPER_GROUPS_CREATE: "whisper:groups:create",

  // Wiki permissions
  WIKI_READ: "wiki:read",
  WIKI_WRITE: "wiki:write",
  WIKI_DELETE: "wiki:delete",
  WIKI_MANAGE_TAGS: "wiki:tags:manage",
} as const;

export const APPOINTMENT_CHECKLIST_TYPE_PERMISSIONS: Partial<
  Record<AppointmentChecklistType, string>
> = {
  [AppointmentChecklistType.CHECKED_IN]: PERMISSIONS.CHECKLIST_CHECKED_IN_WRITE,
  [AppointmentChecklistType.CARE_PLAN]: PERMISSIONS.CHECKLIST_CARE_PLAN_WRITE,
  [AppointmentChecklistType.DOSE_TAKEN]: PERMISSIONS.CHECKLIST_DOSE_TAKEN_WRITE,
  [AppointmentChecklistType.FILLED_FORM]:
    PERMISSIONS.CHECKLIST_FILLED_FORM_WRITE,
  [AppointmentChecklistType.MEASUREMENT_TAKEN]:
    PERMISSIONS.CHECKLIST_MEASUREMENT_TAKEN_WRITE,
  [AppointmentChecklistType.NOTE_TAKEN]: PERMISSIONS.CHECKLIST_NOTE_TAKEN_WRITE,
  [AppointmentChecklistType.PAYMENT_MADE]:
    PERMISSIONS.CHECKLIST_PAYMENT_MADE_WRITE,
  [AppointmentChecklistType.PICTURE_TAKEN]:
    PERMISSIONS.CHECKLIST_PICTURE_TAKEN_WRITE,
  [AppointmentChecklistType.PROFILE_PHOTO]:
    PERMISSIONS.CHECKLIST_PICTURE_TAKEN_WRITE,
  [AppointmentChecklistType.REBOOK]: PERMISSIONS.CHECKLIST_REBOOK_WRITE,
  [AppointmentChecklistType.REQUEST_GFE]:
    PERMISSIONS.CHECKLIST_REQUEST_GFE_WRITE,
  [AppointmentChecklistType.WITH_MA]: PERMISSIONS.CHECKLIST_WITH_MA_WRITE,
  [AppointmentChecklistType.WITH_PROVIDER]:
    PERMISSIONS.CHECKLIST_WITH_PROVIDER_WRITE,
};

const APPOINTMENT_CHECKLIST_UPDATE_PERMISSIONS = Object.values(
  APPOINTMENT_CHECKLIST_TYPE_PERMISSIONS,
);

export function hasAppointmentChecklistPermission(
  user: ProviderSummary | null | undefined,
  type: AppointmentChecklistType | null | undefined,
): boolean {
  if (!user) return false;
  // Signing the note ticks it; nobody can tick it by hand.
  if (type === AppointmentChecklistType.NOTE_SIGNED_OFF) return false;

  const permission = type ? APPOINTMENT_CHECKLIST_TYPE_PERMISSIONS[type] : null;
  if (permission) return hasPermission(user, permission);

  return hasAnyPermission(user, APPOINTMENT_CHECKLIST_UPDATE_PERMISSIONS);
}

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
